"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, ShieldCheck, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const serviceCapabilities = [{
  label: "Commercial Scaffolding",
  to: "/CommercialScaffoldingPage"
}, {
  label: "Residential Projects",
  to: "/ResidentialScaffoldingPage"
}, {
  label: "Roofing & Solar Access",
  to: "/RoofingScaffoldingPage"
}, {
  label: "Specialist Temporary Works",
  to: "/SpecialistAccessScaffoldingPage"
}];
const accreditationBadges = ["NASC Compliant Standards", "CHAS Accredited"];
export default function Camberley() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="heroscaffoldingcamberley" data-nav="dark" className="relative overflow-hidden bg-secondary text-secondary-foreground py-20 lg:py-28">
      {/* Subtle geometric background structure */}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full border border-secondary-foreground/20" />
        <div className="absolute top-1/2 left-10 h-72 w-72 rounded-full border border-secondary-foreground/10" />
        <div className="absolute inset-0 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:24px_24px] opacity-20" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-3xl">
          {/* Location Badge */}
          <motion.div variants={itemVariants} className="mb-6 flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="border-secondary-foreground/30 text-secondary-foreground bg-secondary/50 px-3 py-1 text-xs uppercase tracking-wider font-semibold">
              Surrey Heath & South East Coverage
            </Badge>
          </motion.div>

          {/* Main H1 */}
          <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-secondary-foreground font-default mb-6 leading-tight">
            Scaffolding in Camberley
          </motion.h1>

          {/* Proposition */}
          <motion.p variants={itemVariants} className="text-lg sm:text-xl text-secondary-foreground/80 leading-relaxed mb-8 max-w-2xl font-default">
            Reliable commercial, residential, roofing, and specialist access scaffolding delivered across Camberley and surrounding areas. Built with uncompromising safety standards, clean execution, and dedicated family-led accountability.
          </motion.p>

          {/* Action CTAs */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-12">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-8 h-12 shadow-md transition-colors">
              <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2 justify-center">
                <span>Request a quote</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="border-secondary-foreground/30 text-secondary-foreground hover:text-secondary-foreground hover:bg-secondary-foreground/10 h-12 px-6 bg-transparent transition-colors">
              <a href="tel:01344929900" className="inline-flex items-center gap-2 justify-center text-secondary-foreground hover:text-secondary-foreground">
                <Phone className="h-4 w-4 text-primary" />
                <span>Call 01344 929900</span>
              </a>
            </Button>
          </motion.div>

          {/* Service Capabilities Pills */}
          <motion.div variants={itemVariants} className="pt-6 border-t border-secondary-foreground/15">
            <p className="text-xs uppercase font-semibold text-secondary-foreground/60 tracking-wider mb-3">
              Sector Specialisms
            </p>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {serviceCapabilities.map((service, i) => <Link key={service.to} to={service.to} data-index={i} className="text-xs sm:text-sm font-medium px-3.5 py-1.5 rounded-md bg-secondary-foreground/10 hover:bg-secondary-foreground/20 text-secondary-foreground border border-secondary-foreground/15 transition-colors">
                  {service.label}
                </Link>)}
            </div>
          </motion.div>

          {/* Accreditations / Trust indicators */}
          <motion.div variants={itemVariants} className="mt-8 flex flex-wrap items-center gap-y-2 gap-x-6 text-xs text-secondary-foreground/70">
            {accreditationBadges.map((badge, i) => <div key={badge} data-index={i} className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-primary shrink-0" />
                <span>{badge}</span>
              </div>)}
          </motion.div>
        </motion.div>
      </div>
    </section>;
}